import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import UserDossierModal from './UserDossierModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ShieldCheck, Search, Trash2, Eye, CheckCircle, Clock, Loader2, Globe, MailCheck, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== USERS TAB ====================
function UsersTab({ language }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [dossierUserId, setDossierUserId] = useState(null);
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  useEffect(() => { fetchUsers(); }, [page, search, unverifiedOnly]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/users`, { params: { skip: page * 20, limit: 20, search: search || undefined, email_verified: unverifiedOnly ? false : undefined } });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      fetchUsers();
    } catch (e) { console.error(e); }
  };

  const [resendingId, setResendingId] = useState(null);
  const handleResendVerification = async (userId) => {
    try {
      setResendingId(userId);
      const { data } = await axios.post(`${API}/admin/users/${userId}/resend-verification`);
      toast.success(data?.message || (language === 'lv' ? 'Saite nosūtīta' : 'Link sent'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || (language === 'lv' ? 'Neizdevās nosūtīt' : 'Failed to send'));
    } finally { setResendingId(null); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex gap-4 mb-6 flex-wrap items-center">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C5C5C]" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder={language === 'lv' ? 'Meklēt lietotājus...' : 'Search users...'}
            className="pl-10 h-11 bg-white border-[#E5E4E0]"
            data-testid="search-users-input"
          />
        </div>
        <button
          onClick={() => { setUnverifiedOnly(v => !v); setPage(0); }}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
            unverifiedOnly ? 'bg-amber-500 text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-amber-500'
          }`}
          data-testid="filter-unverified-emails"
        >
          <Clock className="w-4 h-4" />
          {language === 'lv' ? 'Tikai neapstiprināti e-pasti' : 'Unverified emails only'}
        </button>
        <span className="text-sm text-[#5C5C5C] self-center">{total} {language === 'lv' ? 'kopā' : 'total'}</span>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E4E0] bg-[#F3F2EE]">
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#5C5C5C] uppercase">{language === 'lv' ? 'Lietotājs' : 'User'}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#5C5C5C] uppercase">E-pasts</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#5C5C5C] uppercase">{language === 'lv' ? 'Veikals' : 'Shop'}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#5C5C5C] uppercase">{language === 'lv' ? 'Slud.' : 'List.'}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#5C5C5C] uppercase">{language === 'lv' ? 'Loma' : 'Role'}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-[#5C5C5C] uppercase"></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} className="border-b border-[#E5E4E0] last:border-b-0 hover:bg-[#FAFAFA]" data-testid={`user-row-${u._id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#D97736] flex items-center justify-center text-white text-sm font-medium">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-[#121212]">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#5C5C5C]">
                      <div className="flex items-center gap-1.5">
                        <span>{u.email}</span>
                        {u.email_verified ? (
                          <span title={language === 'lv' ? 'E-pasts apstiprināts' : 'Email verified'} data-testid={`email-verified-${u._id}`}>
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          </span>
                        ) : (
                          <span title={language === 'lv' ? 'E-pasts nav apstiprināts' : 'Email not verified'} data-testid={`email-unverified-${u._id}`}>
                            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.shop_profile ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{u.shop_profile.shop_name}</span>
                          {u.shop_profile.is_verified && (
                            <ShieldCheck className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-[#5C5C5C]">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#5C5C5C]">{u.listing_count}</td>
                    <td className="px-4 py-3">
                      <Badge variant={u.role === 'admin' ? 'default' : 'outline'} className={u.role === 'admin' ? 'bg-[#D97736] text-white' : ''}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDossierUserId(u._id)}
                          className="text-[#5C5C5C] hover:text-[#D97736] hover:bg-[#FFF6F0]"
                          title={language === 'lv' ? 'Detalizēts pārskats' : 'Detailed dossier'}
                          data-testid={`view-user-dossier-${u._id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <a
                          href={`/lietotajs/${u._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={language === 'lv' ? 'Atvērt publisko profilu' : 'Open public profile'}
                          data-testid={`open-public-page-${u._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#5C5C5C] hover:text-[#4A6B53] hover:bg-[#EEF4EF] transition-colors"
                        >
                          <Globe className="w-4 h-4" />
                        </a>
                        <a
                          href={`/business-templates?admin_biz=${u._id}&admin_name=${encodeURIComponent(u.name || '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={language === 'lv' ? 'Pārvaldīt uzņēmuma lapas (admin)' : 'Manage business pages (admin)'}
                          data-testid={`manage-business-${u._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#5C5C5C] hover:text-[#D97736] hover:bg-[#FFF6F0] transition-colors"
                        >
                          <Store className="w-4 h-4" />
                        </a>
                        {!u.email_verified && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResendVerification(u._id)}
                            disabled={resendingId === u._id}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title={language === 'lv' ? 'Nosūtīt atkārtotu e-pasta apstiprinājuma saiti' : 'Resend email verification link'}
                            data-testid={`resend-verification-${u._id}`}
                          >
                            {resendingId === u._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                          </Button>
                        )}
                        {u.role !== 'admin' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u._id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{language === 'lv' ? 'Dzēst lietotāju?' : 'Delete user?'}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {language === 'lv' ? `Dzēst ${u.name} un visus viņa datus?` : `Delete ${u.name} and all their data?`}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{language === 'lv' ? 'Atcelt' : 'Cancel'}</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => handleDelete(u._id)}>
                                  {language === 'lv' ? 'Dzēst' : 'Delete'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 20 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E4E0]">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                {language === 'lv' ? 'Iepriekšējā' : 'Previous'}
              </Button>
              <span className="text-sm text-[#5C5C5C]">{page + 1} / {Math.ceil(total / 20)}</span>
              <Button size="sm" variant="outline" disabled={(page + 1) * 20 >= total} onClick={() => setPage(p => p + 1)}>
                {language === 'lv' ? 'Nākamā' : 'Next'}
              </Button>
            </div>
          )}
        </div>
      )}
      <UserDossierModal
        userId={dossierUserId}
        language={language}
        onClose={() => setDossierUserId(null)}
      />
    </div>
  );
}

export default UsersTab;
